/**
 * MyProfile Template
 *
 * Pure presentation component for user profile page
 * Displays user information and handles password change/logout
 *
 * REFACTORED with Facade Pattern!
 *
 * Before: 20+ individual props (user, dialogs, handlers...)
 * After: 1 facade + 2 platform-specific props
 *
 * @package @ideoz/ui
 */

import React, { useState, useCallback } from 'react';
import { Eye, EyeOff, Info, LogOut, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  Button,
  Input,
  Label,
} from '@ideoz/ui';
import {
  calculatePasswordStrength,
  type PasswordStrength,
  type MyProfilePageFacade,
} from '@ideoz/core';
import { AppLayout } from '../layouts/AppLayout';

/**
 * MyProfileTemplate Props
 *
 * SIMPLIFIED with Facade Pattern!
 *
 * Before: 20+ individual props
 * After: 1 facade + 2-3 platform-specific props
 *
 * The facade encapsulates all shared logic following useLandingPageFacade pattern:
 * - State (isLoggedIn, user)
 * - Header configuration & user menu
 * - Navigation (toLanding, toProfile, etc.)
 * - Profile actions (dialogs, change password, etc.)
 * - Logout action
 */
export interface MyProfileTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: MyProfilePageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    openLogin: () => void;
    openRegister: () => void;
    closeDialog: () => void;
  };
}

export default function MyProfileTemplate({
  facade,
  isWeb,
  authDialogState,
}: MyProfileTemplateProps): JSX.Element {
  // ========== Destructure Facade ==========
  const {
    state: { user },
    header: {
      headerProps,
      userMenuOpen,
      onUserMenuClose,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    navigation,
    profile: { logoutDialog, changePasswordDialog },
    logout: { logout: onLogout },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    onRenewSubscription,
  } = facade;

  // Local state for password dialog
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    isValid: false,
    className: '',
    label: 'empty',
  });
  const samePasswordMessage = 'New password must be different from current password';
  const isSamePassword = Boolean(oldPassword && newPassword && oldPassword === newPassword);

  // Password visibility toggle
  const togglePassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  // Password strength checker
  const checkPassword = useCallback((password: string) => {
    setPasswordError('');
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  }, []);

  // Handle password change submission
  const handlePasswordChange = useCallback(async () => {
    if (!oldPassword) {
      setPasswordError('Current password is required');
      return;
    }

    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }

    if (oldPassword === newPassword) {
      setPasswordError(samePasswordMessage);
      return;
    }

    if (!passwordStrength.isValid) {
      setPasswordError('Password is too weak');
      return;
    }

    try {
      await changePasswordDialog.changePassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setPasswordError('');
      setShowPassword(false);
    } catch (error) {
      // Error already shown by facade
    }
  }, [oldPassword, newPassword, passwordStrength.isValid, changePasswordDialog]);

  // Handle password dialog close
  const handlePasswordDialogClose = useCallback(() => {
    changePasswordDialog.close();
    setOldPassword('');
    setNewPassword('');
    setPasswordError('');
    setShowPassword(false);
  }, [changePasswordDialog]);

  // Guard against null user
  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <AppLayout
      currentPath="/profile"
      sidebar={facade.sidebar}
      user={{
        isLoggedin: !!user,
        name: user?.name,
        avatar: user?.avatar,
      }}
      userMenuProps={
        user
          ? {
              points: headerProps.points,
              pointsLabel: 'Ideoz AI Points',
              menuItems: userMenuItems || [],
              socialLinks: userMenuSocialLinks || [],
              onRequestUpgrade,
              isRequestingUpgrade: isRequestingUpgrade || false,
              canRequestUpgrade: (headerProps.points || 0) <= 0,
              isWeb,
            }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? {
              isVisible: true,
              daysUntilExpiration,
              onRenew: onRenewSubscription,
            }
          : undefined
      }
      isWeb={isWeb}
      testId="profile-sidemenu"
      className="data-testid-profile-page"
    >
      <div
        className={`max-w-landing-page mx-auto py-16 ${isWeb ? 'flex flex-col min-h-screen' : ''}`}
        data-testid="profile-page"
      >
        <header className="mb-6 flex flex-col gap-2">
          <h2
            className="text-2xl text-start font-semibold text-gray-800"
            data-testid="projects-list"
          >
            My Profile
          </h2>
          <p className="text-gray-600 text-sm">Manage your profile information</p>
        </header>
        <div className="flex flex-col bg-background rounded-2xl p-8 gap-8">
          {/* User Information Card */}
          <div className="flex flex-col">
            <h3 className="text-lg font-medium text-gray-800">Personal Information</h3>
            <p className="text-gray-600 text-sm">Your account details</p>
          </div>
          <div className="flex flex-col gap-4">
            {/* Name Field */}
            <div data-testid="profile-name-section">
              <Label className="text-sm text-gray-500" data-testid="profile-name-label">
                Name
              </Label>
              <div className="text-lg text-primary-800" data-testid="profile-name-value">
                {user.name}
              </div>
            </div>

            {/* Email Field */}
            <div data-testid="profile-email-section">
              <Label className="text-sm text-gray-500" data-testid="profile-email-label">
                Email
              </Label>
              <div className="text-lg text-primary-800" data-testid="profile-email-value">
                {user.email}
              </div>
            </div>

            {/* Password Section (hidden for Google OAuth) */}
            {user.provider.toLowerCase() !== 'google' && (
              <div className="flex flex-col gap-2" data-testid="profile-password-section">
                <div>
                  <Label className="text-sm text-gray-500" data-testid="profile-password-label">
                    Password
                  </Label>
                  <div className="text-lg text-primary-800" data-testid="profile-password-value">
                    ************
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Logout Button */}
          <div className="flex gap-4 mt-4">
            {user.provider.toLowerCase() !== 'google' && (
              <Button
                variant="secondary-gray"
                size="sm"
                className="text-primary-600 hover:text-primary-800 hover:bg-primary-50"
                data-testid="btn-change-password"
                onClick={changePasswordDialog.open}
              >
                Change Password
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={logoutDialog.open}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-auto"
              data-testid="btn-logout"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog.isOpen} onOpenChange={logoutDialog.close}>
        <DialogContent className="w-dialog-width flex flex-col" data-testid="logout-dialog">
          <DialogHeader className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info size={isWeb ? 22 : 18} strokeWidth={2} color="#0B4A6F" />
              <DialogTitle>Are you sure to quit</DialogTitle>
            </div>
            <button onClick={logoutDialog.close} className="dialog-close-button">
              <X color="#667085" strokeWidth={2.5} />
            </button>
          </DialogHeader>

          <p className="dialog-description">
            You can stay logged easier for next use of Ideoz, easier for you to explore new features
            and experiences
          </p>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={onLogout} data-testid="btn-logout-quit">
              Quit
            </Button>
            <Button
              variant="primary"
              onClick={logoutDialog.close}
              data-testid="btn-logout-continue"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog.isOpen} onOpenChange={handlePasswordDialogClose}>
        <DialogContent
          className="w-dialog-width flex flex-col"
          data-testid="change-password-dialog"
        >
          <DialogHeader className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info size={isWeb ? 22 : 18} strokeWidth={2} color="#0B4A6F" />
              <DialogTitle>Change Password</DialogTitle>
            </div>
            <button
              onClick={handlePasswordDialogClose}
              className="dialog-close-button"
              data-testid="btn-close-change-password"
            >
              <X color="#667085" strokeWidth={2.5} />
            </button>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="oldPassword" data-testid="old-password-label">
                Current Password
              </Label>
              <div className="relative">
              <Input
                id="oldPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Current Password"
                value={oldPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setOldPassword(e.target.value);
                  setPasswordError('');
                }}
                data-testid="input-old-password"
                disabled={changePasswordDialog.isChanging}
              />
               <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={togglePassword}
                  data-testid="btn-toggle-password"
                  disabled={changePasswordDialog.isChanging}
                >
                  {!showPassword ? (
                    <EyeOff size={22} strokeWidth={2.5} color="#98A2B3" />
                  ) : (
                    <Eye size={22} strokeWidth={2.5} color="#98A2B3" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" data-testid="new-password-label">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    checkPassword(value);
                  }}
                  className="pr-10"
                  data-testid="input-new-password"
                  disabled={changePasswordDialog.isChanging}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={togglePassword}
                  data-testid="btn-toggle-password"
                  disabled={changePasswordDialog.isChanging}
                >
                  {!showPassword ? (
                    <EyeOff size={22} strokeWidth={2.5} color="#98A2B3" />
                  ) : (
                    <Eye size={22} strokeWidth={2.5} color="#98A2B3" />
                  )}
                </Button>
              </div>

              {(isSamePassword ? samePasswordMessage : passwordError) && (
                <p className="text-base text-red-600" data-testid="password-error">
                  {isSamePassword ? samePasswordMessage : passwordError}
                </p>
              )}

              <p className="text-xs text-gray-500">
                Password must contain lowercase, uppercase, number, special character, and be longer
                than 7 characters
              </p>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="space-y-1" data-testid="password-strength-indicator">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Password strength</span>
                    <span
                      className={`font-medium ${passwordStrength.isValid ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      {passwordStrength.score < 3
                        ? 'Weak'
                        : passwordStrength.score < 5
                          ? 'Medium'
                          : 'Strong'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.className}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handlePasswordDialogClose}
              data-testid="btn-cancel-change-password"
              disabled={changePasswordDialog.isChanging}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePasswordChange}
              disabled={
                !oldPassword ||
                isSamePassword ||
                !passwordStrength.isValid ||
                !newPassword ||
                changePasswordDialog.isChanging
              }
              data-testid="btn-submit-change-password"
            >
              {changePasswordDialog.isChanging ? 'Changing...' : 'Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
